import * as public_env from '$app/env/public';
import StoryBlockModel from '#lib/StoryBlockModel.svelte.js';
import StoryBlock from '#lib/StoryBlock.svelte';

// ENTRIES
import ImageEntryModel from '#lib/ImageEntryModel.svelte.js';
import ImageEntry from '#lib/ImageEntry.svelte';

import PageEntryModel from '#lib/PageEntryModel.svelte.js';
import PageEntry from '#lib/PageEntry.svelte';

import CollectionEntryModel from '#lib/CollectionEntryModel.svelte.js';
import CollectionEntry from '#lib/CollectionEntry.svelte';

import EventEntryModel from '#lib/EventEntryModel.svelte.js';
import EventEntry from '#lib/EventEntry.svelte';

// CUSTOM_CODE_STARTS (PATCH_WORK)
import ResourceModel from '#lib/ResourceModel.svelte.js';
import ResourceEntry from '#lib/ResourceEntry.svelte';
// CUSTOM_CODE_ENDS (PATCH_WORK)

// TOOLS
// The visibility control (replace + crop live as overlays on the image canvas)
import EntryVisibilityTool from '#lib/EntryVisibilityTool.svelte'
import EntryTagsTool from '#lib/EntryTagsTool.svelte';
// The entry settings popover (visibility, tags, preview card). Formerly named
// PreviewTool although its button has always said "Settings".
import EntrySettingsTool from '#lib/EntrySettingsTool.svelte';


// ENTRIES
export const ENTRIES = {
  page: { 
    id: 'page',
    icon: 'page',
    label_i18n: 'entry_label_page',
    label_i18n_plural: 'entry_label_page_plural',
    model: PageEntryModel, 
    component: PageEntry, 
  },
  image: { 
    id: 'image',
    icon: 'image',
    label_i18n: 'entry_label_image',
    label_i18n_plural: 'entry_label_image_plural',
    model: ImageEntryModel, 
    component: ImageEntry, 
  },
  event: {
    id: 'event',
    icon: 'calendar',
    label_i18n: 'entry_label_event',
    label_i18n_plural: 'entry_label_event_plural',
    model: EventEntryModel, 
    component: EventEntry, 
  },
  // CUSTOM_CODE_STARTS (PATCH_WORK)
  resource: {
    id: 'resource',
    icon: 'booking',
    label_i18n: 'resource_label',
    label_i18n_plural: 'resource_label_plural',
    model: ResourceModel, 
    component: ResourceEntry, 
  },
  // CUSTOM_CODE_ENDS (PATCH_WORK)
  collection: { 
    id: 'collection',
    icon: 'bookmarks',
    label_i18n: 'entry_label_collection',
    label_i18n_plural: 'entry_label_collection_plural',
    model: CollectionEntryModel, 
    component: CollectionEntry, 
  },
};

export const BLOCKS = {
  story: {
    model: StoryBlockModel,
    component: StoryBlock,
    label_i18n: 'story',
    select_type: 'outline',    
    insert_after_block_type: 'story',
  },
};

export const ROLES_AND_ACCESS_LEVELS = [
  { role: 'admin', level: 4 },
  { role: 'member', level: 3 },
  { role: 'contact', level: 2 },
  { role: 'public', level: 1 }
];

// Roles that can be assigned to user records ('public' is virtual, not stored)
export const VALID_USER_ROLES = new Set(
  ROLES_AND_ACCESS_LEVELS.filter(r => r.role !== 'public').map(r => r.role)
);

/**
 * VISIBILITY SYSTEM DESIGN
 * 
 * This system defines who can see what content based on visibility levels and user roles.
 * 
 * VISIBILITY LEVELS:
 * - 'public': Everyone can see (including non-logged-in users)
 * - 'member': Members and admins can see  
 * - 'internal': Only admins can see
 * - 'private': Only the creator can see + admins can see all private entries
 * 
 * SEMANTIC USAGE:
 * - 'public': General content meant for everyone
 * - 'member': Admin-created content for all members (announcements, shared resources)
 * - 'internal': Admin-only content (system info, sensitive data)
 * - 'private': Personal content (member's personal collections) - admins have full access
 * 
 * DEFAULT CREATION BEHAVIOR:
 * - Admin-created entries: Default to 'public' (they decide what's shared)
 * - Member-created collections: Default to 'private' (personal by default)
 * - Members who want to share can manually change to 'public'
 * 
 * ADMIN ACCESS:
 * - Admins can see all private entries (for moderation/support)
 * - This provides full administrative oversight capability
 * 
 * NOTE: 'private' entries are intentionally excluded from this config as they
 * are handled with special owner-only + admin access logic in the SQL queries.
 */
export const ROLE_VISIBILITY_ACCESS = {
  'admin': ['public', 'member', 'internal'],
  'member': ['public', 'member'],
  'contact': ['public'],
  'public': ['public']
};

/**
 * Preview visibility access per role.
 *
 * NOTE: 'private' previews are intentionally excluded and handled with
 * owner/admin checks in `has_access_to_entry_preview()`.
 */
export const ROLE_PREVIEW_VISIBILITY_ACCESS = {
  'admin': ['public', 'member', 'internal'],
  'member': ['public', 'member'],
  'contact': ['public', 'member'],
  'public': ['public', 'member']
};

export const DISPLAY_TOOLS = {};

export const ENTRY_TOOLS = {
  page: [
    {component: EntrySettingsTool},
    {component: EntryVisibilityTool},
    {component: EntryTagsTool},
  ],
  image: [
    {component: EntrySettingsTool},
    {component: EntryVisibilityTool},
    {component: EntryTagsTool},
  ],
  event: [
    {component: EntrySettingsTool},
    {component: EntryVisibilityTool},
    {component: EntryTagsTool},
  ],
  collection: [
    {component: EntrySettingsTool},
    {component: EntryVisibilityTool},
    {component: EntryTagsTool},
  ],
  // CUSTOM_CODE_STARTS (PATCH_WORK)
  resource: [
    {component: EntrySettingsTool},
    {component: EntryVisibilityTool},
  ],
  // CUSTOM_CODE_ENDS (PATCH_WORK)
};

// Hard-coded for now
export const MODULES = [
  {
    value: 'calendar',
    title: 'Calendar',
    description: 'The calendar module',
    icon: 'calendar'
  },
  {
    value: 'all',
    title: 'All',
    description: 'All Entries',
    icon: 'list'
  }
];

export const FIXED_ROUTES = [
  'calendar',
  'messages',
  'login',
  'posters',
  'account',
  'users',
]

export const NAV_ITEM_TYPES = [
  ...FIXED_ROUTES,
  'entry',
  'user_profile',
  'weblink',
  'phone',
  'email',
  'spacer',
  'separator',
  'mobile_menu_breakpoint'
];

const POSTER_EDITORS = public_env.PUBLIC_ALLOW_MEMBER_POSTERS === 'true' ? ['admin', 'member'] : ['admin'];

export const ENTRY_ACCESS_CONTROL = {
  collection: {
    create: ['admin', 'member'],
    update: ['admin', 'member'], // Members can only update their own collections
    delete: ['admin', 'member']  // Members can only delete their own collections
  },
  page: {
    create: ['admin'],
    update: ['admin'],
    delete: ['admin']
  },
  event: {
    create: ['admin'],
    update: ['admin'],
    delete: ['admin']
  },
  image: {
    create: ['admin'],
    update: ['admin'],
    delete: ['admin']
  },
  // Admin-only unless the deployment opts members in (the server checks this
  // via can_manage_posters in api.js). Members may always delete their own.
  poster: {
    create: POSTER_EDITORS,
    update: POSTER_EDITORS, // Members can only update their own posters
    delete: ['admin', 'member'], // Members can only delete their own posters
  },
  // CUSTOM_CODE_STARTS (PATCH_WORK)
  resource: {
    create: ['admin'],
    update: ['admin'], // Members can only update their own resources
    delete: ['admin']  // Members can only delete their own resources
  },
  // CUSTOM_CODE_ENDS (PATCH_WORK)
};

export const ROUTE_ACCESS_CONTROL = {
  users: ['admin'],  // Currently admin-only but could be expanded
  settings: ['admin'],
};

export const LANGUAGE_SPECIFIC_CHARACTERS = {
  de: 'äöüß',
  fr: 'àâçéèêëîïôùûüæœ',
  es: 'áéíóúüñ',
  it: 'àèéìíòù',
  nl: 'ëïüéóá',
  pl: 'ąćęłńóśźż',
  pt: 'áâãàçéêíóôõúü',
  ru: 'абвгдеёжзийклмнопрстуфхцчшщъыьэюя',
  sv: 'åäö',
  tr: 'çğıİöşü',
  gr: 'αβγδεζηθικλμνξοπρστυφχψωάέήίόύώϊϋΐΰ',
  cs: 'áčďéěíňóřšťúůýž',
  hu: 'áéíóöőúüű',
  ro: 'ăâîșț',
  bg: 'абвгдежзийклмнопрстуфхцчшщъьюя',
};

/**
 * Available webhook event types
 */
export const WEBHOOK_EVENTS = {
  USER_SIGNUP: 'user.signup',
  USER_SIGNIN: 'user.signin',
  USER_UPDATE: 'user.update',
  // Event registration webhooks
  EVENT_REGISTRATION_NEW: 'event_registration.new',
  EVENT_REGISTRATION_UPDATE: 'event_registration.update',
  EVENT_REGISTRATION_CANCEL: 'event_registration.cancel',
};

// Smallest chargeable price for a paid event, in the currency's minor units.
// Stripe rejects charges below ~0.50 in the major currencies; a price set
// between 1 and this value would look paid to members but fail at checkout.
export const MIN_EVENT_PRICE_AMOUNT = 50;

// Stripe takes and reports whole units for zero-decimal currencies (¥500 is
// 500, not 50000), while this app stores every price in 1/100 units. The
// Stripe boundary converts; the price checks below judge in stored units.
export const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf',
  'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf',
]);
export function is_zero_decimal_currency(currency) {
  return typeof currency === 'string' && ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase());
}

// The smallest price the app accepts, in stored (1/100) units: ¥50 is stored
// as 5000, so a zero-decimal currency needs the minimum scaled up.
export function get_min_event_price_amount(currency) {
  return is_zero_decimal_currency(currency) ? MIN_EVENT_PRICE_AMOUNT * 100 : MIN_EVENT_PRICE_AMOUNT;
}

// Whether a stored price can be charged as configured: at least the minimum,
// and — for a currency without minor units — a whole number of units, since
// Stripe would otherwise round ¥1.49 to ¥1 and record a different amount.
export function is_valid_event_price_amount(price_amount, currency) {
  if (!Number.isInteger(price_amount) || price_amount < get_min_event_price_amount(currency)) return false;
  return !is_zero_decimal_currency(currency) || price_amount % 100 === 0;
}

// Registration settings an event saved before these keys existed falls back
// to. Shared by the event model (what the admin and member see) and the
// server's registration endpoints (what they enforce), so both judge the same
// event.
export const EVENT_REGISTRATION_DEFAULTS = {
  registration_mode: 'internal',
  max_guests_per_registration: 5,
};

// Attendee field caps, enforced by the server and checked per field by the
// client first (the server rejects a whole batch on a violation).
export const MAX_EVENT_REGISTRATION_NAME_LENGTH = 255;
export const MAX_EVENT_REGISTRATION_EMAIL_LENGTH = 255;
export const MAX_EVENT_REGISTRATION_NOTES_LENGTH = 2000;
// CUSTOM_CODE_STARTS (PATCH_WORK)
export const RESOURCE_TYPE_BOOKING_OPTIONS = {
  meeting_room: ['half_hourly'],
  flex_desk: ['daily'],
  open_day: ['daily'],
  fixed_desk: ['subscription'],
  office: ['subscription'],
  equipment: ['half_hourly'],
  addon: ['subscription']
};

export const RESOURCE_TYPE_HAS_AVAILABLE_QUANTITY_OPTION = ['flex_desk', 'fixed_desk', 'office', 'addon', 'open_day'];
export const RESOURCE_TYPE_HAS_MAX_PRICE_OPTION = ['flex_desk', 'meeting_room'];
export const RESOURCE_TYPE_ENABLES_CREDITS_AND_DISCOUNTS = ['fixed_desk', 'office'];

export const RESOURCE_TYPE_PRICING_CONFIG = {
  meeting_room: {
    price_unit: 'half_hour',
    max_price_unit: 'day'
  },
  flex_desk: {
    price_unit: 'day',
    max_price_unit: 'month'
  },
  open_day: {
    price_unit: 'day',
  },
  fixed_desk: {
    price_unit: 'month'
  },
  office: {
    price_unit: 'month'
  },
  addon: {
    price_unit: 'month'
  }
};
// CUSTOM_CODE_ENDS (PATCH_WORK)
